/**
 * Annotation Panel Component
 * Lists annotations for the current clip, with tag filter and delete
 */

import React from 'react';
import { useClipDataStore } from '../../stores/clipDataStore';
import type { Annotation } from '../../lib/api-clip-monitor';
import { format } from 'date-fns';

interface AnnotationPanelProps {
  onAddAnnotation: () => void;
  onEditAnnotation: (annotation: Annotation) => void;
}

const TAG_COLORS: Record<string, string> = {
  'false-positive': 'bg-red-100 text-red-800',
  'pooling-flicker': 'bg-amber-100 text-amber-800',
  'sensor-issue': 'bg-orange-100 text-orange-800',
  'observation': 'bg-blue-100 text-blue-800',
  'error': 'bg-red-100 text-red-800',
  default: 'bg-gray-100 text-gray-800',
};

const getTagClass = (tag: string): string => {
  return TAG_COLORS[tag] || TAG_COLORS.default;
};

export const AnnotationPanel: React.FC<AnnotationPanelProps> = ({ onAddAnnotation, onEditAnnotation }) => {
  const {
    annotations,
    loadingAnnotations,
    annotationTagFilter,
    selectedDiaperCounter,
    setAnnotationTagFilter,
    deleteAnnotation,
    goToAnnotation,
  } = useClipDataStore();

  const filteredAnnotations = React.useMemo(() => {
    let list = annotations;
    if (annotationTagFilter) {
      list = list.filter((a) => a.tags.includes(annotationTagFilter));
    }
    if (selectedDiaperCounter !== null) {
      list = list.filter(
        (a) => a.diaperCounter === null || a.diaperCounter === selectedDiaperCounter
      );
    }
    return [...list].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [annotations, annotationTagFilter, selectedDiaperCounter]);

  const allTags = React.useMemo(() => {
    const set = new Set<string>();
    annotations.forEach((a) => a.tags.forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [annotations]);

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm('Delete this annotation?')) {
      try {
        await deleteAnnotation(id);
      } catch (err) {
        console.error('Failed to delete annotation:', err);
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Annotations</h3>
        <button
          onClick={onAddAnnotation}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
        >
          + Add Annotation
        </button>
      </div>

      {/* Tag filter */}
      {allTags.length > 0 && (
        <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap gap-1">
          <span className="text-xs text-gray-500 mr-1 self-center">Filter:</span>
          <button
            onClick={() => setAnnotationTagFilter(null)}
            className={`px-2 py-0.5 rounded text-xs font-medium ${
              annotationTagFilter === null ? 'bg-blue-200 text-blue-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setAnnotationTagFilter(annotationTagFilter === tag ? null : tag)}
              className={`px-2 py-0.5 rounded text-xs font-medium ${getTagClass(tag)} ${
                annotationTagFilter === tag ? 'ring-2 ring-blue-400' : ''
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      <div className="p-4 max-h-64 overflow-y-auto">
        {loadingAnnotations ? (
          <p className="text-sm text-gray-500">Loading annotations...</p>
        ) : filteredAnnotations.length === 0 ? (
          <p className="text-sm text-gray-500">
            {annotations.length === 0
              ? 'No annotations yet. Add one to tag observations or errors.'
              : 'No annotations match the current filter.'}
          </p>
        ) : (
          <ul className="space-y-3">
            {filteredAnnotations.map((ann) => (
              <AnnotationItem
                key={ann.id}
                annotation={ann}
                onDelete={handleDelete}
                onGoTo={() => goToAnnotation(ann)}
                onEdit={() => onEditAnnotation(ann)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

interface AnnotationItemProps {
  annotation: Annotation;
  onDelete: (e: React.MouseEvent, id: string) => void;
  onGoTo: () => void;
  onEdit: () => void;
}

const AnnotationItem: React.FC<AnnotationItemProps> = ({ annotation, onDelete, onGoTo, onEdit }) => {
  const hasLocation = annotation.diaperCounter !== null || annotation.timeRange || annotation.measurementTimestamp;
  const timeLabel = annotation.measurementTimestamp
    ? format(new Date(annotation.measurementTimestamp), 'HH:mm:ss')
    : annotation.timeRange
    ? `${format(new Date(annotation.timeRange.start), 'HH:mm')} – ${format(new Date(annotation.timeRange.end), 'HH:mm')}`
    : annotation.diaperCounter !== null
    ? `Diaper #${annotation.diaperCounter}`
    : 'General';

  return (
    <li className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-1 mb-1">
            {annotation.tags.map((tag) => (
              <span
                key={tag}
                className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${getTagClass(tag)}`}
              >
                {tag}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-800">{annotation.text}</p>
          <p className="text-xs text-gray-500 mt-1">{timeLabel}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {hasLocation && (
            <button
              onClick={onGoTo}
              className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
              title="Go to this point on timeline"
            >
              📍 Go to
            </button>
          )}
          <button
            onClick={onEdit}
            className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
            title="Edit annotation"
          >
            ✎ Edit
          </button>
          <button
            onClick={(e) => onDelete(e, annotation.id)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1 text-sm"
            title="Delete"
          >
            ×
          </button>
        </div>
      </div>
    </li>
  );
};
